import React, { useRef, useCallback, useState } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  useReactFlow,
  Background,
  Node,
  Edge,
  Connection,
  BackgroundVariant,
  Handle,
  Position,
  NodeProps,
  MiniMap,
} from 'reactflow';
import 'reactflow/dist/style.css';
import './FlowPage.css';

import Sidebar from '../components/Sidebar';
import { DnDProvider } from '../components/DnDContext';

/**
 * 开始节点组件 - 简洁风格
 */
const StartNode: React.FC<NodeProps> = ({ data, selected, id }) => {
  const variables = data.variables || [];
  
  return (
    <div className={`custom-node start-node ${selected ? 'selected' : ''}`}>
      {/* 开始节点只有右侧连接点 */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
      
      {/* 节点内容 */}
      <div className="node-content">
        <div className="start-node-header">
          <div className="node-icon-wrapper">
            <div className="node-icon">🏠</div>
          </div>
          <div className="node-info">
            <div className="node-text">{data.label}</div>
            <div className="node-description">工作流开始</div>
          </div>
        </div>
        
        {/* 变量列表显示 - 紧凑版 */}
        {variables.length > 0 && (
          <div className="start-node-variables">
            <div className="variables-compact-list">
              {variables.slice(0, 3).map((variable: any, index: number) => (
                <div key={index} className="variable-compact-item">
                  <span className="variable-compact-name">{variable.name}</span>
                  <span className="variable-compact-type">{variable.type}</span>
                </div>
              ))}
              {variables.length > 3 && (
                <div className="variable-compact-item more-variables">
                  <span className="variable-compact-name">还有 {variables.length - 3} 个变量</span>
                  <span className="variable-compact-type">⋯</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * 结束节点组件 - 简洁风格
 */
const EndNode: React.FC<NodeProps> = ({ data, selected, id }) => {
  return (
    <div className={`custom-node end-node ${selected ? 'selected' : ''}`}>
      {/* 结束节点只有左侧连接点 */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
      
      {/* 节点内容 */}
      <div className="node-content">
        <div className="end-node-wrapper">
          <div className="node-icon-wrapper">
            <div className="node-icon">⏹</div>
          </div>
          <div className="node-info">
            <div className="node-text">{data.label}</div>
            <div className="node-description">工作流结束</div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 大模型节点组件 - 简洁风格
 */
const LLMNode: React.FC<NodeProps> = ({ data, selected, id }) => {
  const modelConfig = data.modelConfig || {};
  const model = modelConfig.model || 'gpt-3.5-turbo';
  
  return (
    <div className={`custom-node llm-node ${selected ? 'selected' : ''}`}>
      {/* 左侧连接点 */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
      
      {/* 节点内容 */}
      <div className="node-content">
        <div className="llm-node-header">
          <div className="node-icon-wrapper">
            <div className="node-icon">🤖</div>
          </div>
          <div className="llm-node-info">
            <div className="node-text">{data.label}</div>
            <div className="llm-model-name">{model}</div>
          </div>
        </div>
      </div>
      
      {/* 右侧连接点 */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
    </div>
  );
};

/**
 * 代码执行节点组件 - 简洁风格
 */
const CodeNode: React.FC<NodeProps> = ({ data, selected, id }) => {
  const codeConfig = data.codeConfig || {};
  const language = codeConfig.language || 'Python';
  const codePreview = codeConfig.code || '';
  
  return (
    <div className={`custom-node code-node ${selected ? 'selected' : ''}`}>
      {/* 左侧连接点 */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
      
      {/* 节点内容 */}
      <div className="node-content">
        <div className="code-node-wrapper">
          <div className="code-node-header">
            <div className="node-icon-wrapper">
              <div className="node-icon">&lt;/&gt;</div>
            </div>
            <div className="code-node-info">
              <div className="node-text">{data.label}</div>
              <div className="code-language">{language}</div>
            </div>
          </div>
          
          {/* 代码预览区域 */}
          {codePreview && (
            <div className="code-preview">
              <div className="code-preview-content">
                {codePreview.length > 50 
                  ? `${codePreview.substring(0, 50)}...` 
                  : codePreview
                }
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 右侧连接点 */}
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
    </div>
  );
};

/**
 * 判断节点组件 - 简洁风格，支持多条件和多分支连线
 */
const ConditionNode: React.FC<NodeProps> = ({ data, selected, id }) => {
  const conditionConfig = data.conditionConfig || {};
  const conditions = conditionConfig.conditions || [];
  const conditionCount = conditions.length || 1;
  
  // 计算分支数量，至少有IF和ELSE两个分支
  const branchCount = Math.max(2, conditionCount + 1);
  
  return (
    <div className={`custom-node condition-node ${selected ? 'selected' : ''}`}>
      {/* 左侧连接点 */}
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          background: '#374151',
          width: '8px',
          height: '8px',
          border: 'none',
        }}
      />
      
      {/* 动态生成多个右侧连接点 */}
      {Array.from({ length: branchCount }, (_, index) => {
        const positionPercent = (100 / (branchCount + 1)) * (index + 1);
        return (
          <Handle
            key={`branch-${index}`}
            type="source"
            position={Position.Right}
            id={`branch-${index}`}
            style={{
              background: '#374151',
              width: '8px',
              height: '8px',
              border: 'none',
              top: `${positionPercent}%`,
            }}
          />
        );
      })}
      
      {/* 节点内容 */}
      <div className="node-content">
        <div className="condition-node-wrapper">
          <div className="condition-node-header">
            <div className="node-icon-wrapper">
              <div className="node-icon">IF</div>
            </div>
            <div className="condition-node-info">
              <div className="node-text">{data.label}</div>
              <div className="condition-count">
                {conditionCount === 1 ? '1个条件' : `${conditionCount}个条件`}
              </div>
            </div>
          </div>
          
          {/* 条件分支显示 */}
          <div className="condition-branches">
            {Array.from({ length: Math.min(branchCount, 4) }, (_, index) => (
              <div key={index} className={`branch-item ${index === branchCount - 1 ? 'else-branch' : 'if-branch'}`}>
                <span className="branch-icon">
                  {index === branchCount - 1 ? '✗' : '✓'}
                </span>
                <span className="branch-text">
                  {index === branchCount - 1 
                    ? 'ELSE 默认分支' 
                    : `IF 条件${index + 1}`
                  }
                </span>
                <div className="branch-connector"></div>
              </div>
            ))}
            {branchCount > 4 && (
              <div className="branch-item more-branches">
                <span className="branch-icon">⋯</span>
                <span className="branch-text">还有 {branchCount - 4} 个分支</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * 节点配置面板组件
 */
const NodeConfigPanel: React.FC<{
  selectedNode: Node | null;
  onClose: () => void;
  onUpdateNode: (nodeId: string, data: any) => void;
}> = ({ selectedNode, onClose, onUpdateNode }) => {
  const [nodeName, setNodeName] = useState(selectedNode?.data?.label || '');
  const [nodeDescription, setNodeDescription] = useState(selectedNode?.data?.description || '');
  const [variables, setVariables] = useState(selectedNode?.data?.variables || []);
  
  // 大模型配置状态
  const [modelConfig, setModelConfig] = useState(selectedNode?.data?.modelConfig || {
    model: 'gpt-3.5-turbo',
    temperature: 0.7,
    maxTokens: 1000,
    systemPrompt: '',
    userPrompt: '',
  });

  // 代码执行配置状态
  const [codeConfig, setCodeConfig] = useState(selectedNode?.data?.codeConfig || {
    language: 'Python',
    code: '# 请输入您的代码\ndef main():\n    return {"result": "Hello World"}',
    timeout: 30,
  });

  // 条件判断配置状态
  const [conditionConfig, setConditionConfig] = useState(selectedNode?.data?.conditionConfig || {
    conditions: [
      {
        variable: '',
        operator: 'contains',
        value: '',
        logicalOperator: 'and',
      }
    ],
  });

  // 自动保存功能
  React.useEffect(() => {
    if (selectedNode && (
      nodeName !== selectedNode.data?.label || 
      nodeDescription !== selectedNode.data?.description ||
      JSON.stringify(variables) !== JSON.stringify(selectedNode.data?.variables || []) ||
      JSON.stringify(modelConfig) !== JSON.stringify(selectedNode.data?.modelConfig || {}) ||
      JSON.stringify(codeConfig) !== JSON.stringify(selectedNode.data?.codeConfig || {}) ||
      JSON.stringify(conditionConfig) !== JSON.stringify(selectedNode.data?.conditionConfig || {})
    )) {
      const timer = setTimeout(() => {
        onUpdateNode(selectedNode.id, {
          ...selectedNode.data,
          label: nodeName,
          description: nodeDescription,
          variables: variables,
          modelConfig: modelConfig,
          codeConfig: codeConfig,
          conditionConfig: conditionConfig,
        });
      }, 500); // 500ms 延迟自动保存

      return () => clearTimeout(timer);
    }
  }, [nodeName, nodeDescription, variables, modelConfig, codeConfig, conditionConfig, selectedNode, onUpdateNode]);

  // 当选中节点变化时，更新表单数据
  React.useEffect(() => {
    if (selectedNode) {
      setNodeName(selectedNode.data?.label || '');
      setNodeDescription(selectedNode.data?.description || '');
      setVariables(selectedNode.data?.variables || []);
      setModelConfig(selectedNode.data?.modelConfig || {
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        maxTokens: 1000,
        systemPrompt: '',
        userPrompt: '',
      });
      setCodeConfig(selectedNode.data?.codeConfig || {
        language: 'Python',
        code: '# 请输入您的代码\ndef main():\n    return {"result": "Hello World"}',
        timeout: 30,
      });
      setConditionConfig(selectedNode.data?.conditionConfig || {
        conditions: [
          {
            variable: '',
            operator: 'contains',
            value: '',
            logicalOperator: 'and',
          }
        ],
      });
    }
  }, [selectedNode]);

  /**
   * 添加新变量
   */
  const addVariable = () => {
    const newVariable = {
      name: `变量${variables.length + 1}`,
      type: 'text',
      description: '',
      required: true,
    };
    setVariables([...variables, newVariable]);
  };

  /**
   * 删除变量
   */
  const removeVariable = (index: number) => {
    const newVariables = variables.filter((variable: any, i: number) => i !== index);
    setVariables(newVariables);
  };

  /**
   * 更新变量
   */
  const updateVariable = (index: number, field: string, value: any) => {
    const newVariables = [...variables];
    newVariables[index] = { ...newVariables[index], [field]: value };
    setVariables(newVariables);
  };

  /**
   * 更新模型配置
   */
  const updateModelConfig = (field: string, value: any) => {
    setModelConfig((prev: any) => ({ ...prev, [field]: value }));
  };

  /**
   * 更新代码配置
   */
  const updateCodeConfig = (field: string, value: any) => {
    setCodeConfig((prev: any) => ({ ...prev, [field]: value }));
  };

  /**
   * 更新条件配置
   */
  const updateConditionConfig = (field: string, value: any) => {
    setConditionConfig((prev: any) => ({ ...prev, [field]: value }));
  };

  /**
   * 添加新条件
   */
  const addCondition = () => {
    const newCondition = {
      variable: '',
      operator: 'contains',
      value: '',
      logicalOperator: 'and',
    };
    setConditionConfig((prev: any) => ({
      ...prev,
      conditions: [...prev.conditions, newCondition]
    }));
  };

  /**
   * 删除条件
   */
  const removeCondition = (index: number) => {
    setConditionConfig((prev: any) => ({
      ...prev,
      conditions: prev.conditions.filter((_: any, i: number) => i !== index)
    }));
  };

  /**
   * 更新特定条件
   */
  const updateCondition = (index: number, field: string, value: any) => {
    setConditionConfig((prev: any) => ({
      ...prev,
      conditions: prev.conditions.map((condition: any, i: number) => 
        i === index ? { ...condition, [field]: value } : condition
      )
    }));
  };

  if (!selectedNode) return null;

  return (
    <div className="node-config-panel">
      <div className="config-panel-header">
        <h3>节点配置</h3>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>
      
      <div className="config-panel-content">
        <div className="config-section">
          <label>节点名称</label>
          <input
            type="text"
            value={nodeName}
            onChange={(e) => setNodeName(e.target.value)}
            placeholder="请输入节点名称"
          />
        </div>
        
        <div className="config-section">
          <label>节点描述</label>
          <textarea
            value={nodeDescription}
            onChange={(e) => setNodeDescription(e.target.value)}
            placeholder="请输入节点描述"
            rows={4}
          />
        </div>
        
        <div className="config-section">
          <label>节点类型</label>
          <div className="node-type-info">
            {selectedNode.type === 'start' && '🏠 开始节点'}
            {selectedNode.type === 'end' && '⏻ 结束节点'}
            {selectedNode.type === 'llm' && 'LLM 大模型节点'}
            {selectedNode.type === 'code' && '</> 代码执行节点'}
            {selectedNode.type === 'condition' && 'IF 判断节点'}
          </div>
        </div>

        {/* 开始节点的变量配置 */}
        {selectedNode.type === 'start' && (
          <div className="config-section">
            <div className="variables-config-header">
              <label>输入变量</label>
              <button className="add-variable-btn" onClick={addVariable}>
                + 添加变量
              </button>
            </div>
            
            {variables.length === 0 ? (
              <div className="no-variables">
                <p>暂无输入变量，点击"添加变量"开始配置</p>
              </div>
            ) : (
              <div className="variables-config-list">
                {variables.map((variable: any, index: number) => (
                  <div key={index} className="variable-config-item">
                    <div className="variable-config-header">
                      <span className="variable-index">变量 {index + 1}</span>
                      <button 
                        className="remove-variable-btn"
                        onClick={() => removeVariable(index)}
                      >
                        ×
                      </button>
                    </div>
                    
                    <div className="variable-config-fields">
                      <div className="field-group">
                        <label>变量名称</label>
                        <input
                          type="text"
                          value={variable.name}
                          onChange={(e) => updateVariable(index, 'name', e.target.value)}
                          placeholder="请输入变量名称"
                        />
                      </div>
                      
                      <div className="field-group">
                        <label>变量类型</label>
                        <select
                          value={variable.type}
                          onChange={(e) => updateVariable(index, 'type', e.target.value)}
                          aria-label="选择变量类型"
                        >
                          <option value="text">文本</option>
                          <option value="number">数字</option>
                          <option value="boolean">布尔值</option>
                          <option value="array">数组</option>
                          <option value="object">对象</option>
                        </select>
                      </div>
                      
                      <div className="field-group">
                        <label>变量描述</label>
                        <textarea
                          value={variable.description}
                          onChange={(e) => updateVariable(index, 'description', e.target.value)}
                          placeholder="请输入变量描述"
                          rows={2}
                        />
                      </div>
                      
                      <div className="field-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={variable.required}
                            onChange={(e) => updateVariable(index, 'required', e.target.checked)}
                          />
                          <span>必填变量</span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 大模型节点的配置 */}
        {selectedNode.type === 'llm' && (
          <>
            <div className="config-section">
              <label>模型选择</label>
              <select
                value={modelConfig.model}
                onChange={(e) => updateModelConfig('model', e.target.value)}
                aria-label="选择大模型"
              >
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                <option value="gpt-4">GPT-4</option>
                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                <option value="claude-3-haiku">Claude 3 Haiku</option>
                <option value="claude-3-sonnet">Claude 3 Sonnet</option>
                <option value="claude-3-opus">Claude 3 Opus</option>
              </select>
            </div>

            <div className="config-section">
              <label>系统提示词 (System Prompt)</label>
              <textarea
                value={modelConfig.systemPrompt}
                onChange={(e) => updateModelConfig('systemPrompt', e.target.value)}
                placeholder="请输入系统提示词，用于设定AI的角色和行为规范"
                rows={4}
              />
              <div className="config-hint">定义AI助手的角色、行为和回答风格</div>
            </div>

            <div className="config-section">
              <label>用户提示词 (User Prompt)</label>
              <textarea
                value={modelConfig.userPrompt}
                onChange={(e) => updateModelConfig('userPrompt', e.target.value)}
                placeholder="请输入用户提示词模板，可以使用 {{变量名}} 来引用输入变量"
                rows={6}
              />
              <div className="config-hint">用户输入的提示词模板，支持变量替换</div>
            </div>

            <div className="config-section">
              <label>温度 (Temperature)</label>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={modelConfig.temperature}
                  onChange={(e) => updateModelConfig('temperature', parseFloat(e.target.value))}
                  className="temperature-slider"
                  aria-label="设置温度值"
                />
                <span className="slider-value">{modelConfig.temperature}</span>
              </div>
              <div className="config-hint">控制输出的随机性，0为确定性，2为最随机</div>
            </div>

            <div className="config-section">
              <label>最大令牌数 (Max Tokens)</label>
              <input
                type="number"
                min="1"
                max="4000"
                value={modelConfig.maxTokens}
                onChange={(e) => updateModelConfig('maxTokens', parseInt(e.target.value))}
                placeholder="请输入最大令牌数"
              />
              <div className="config-hint">限制模型输出的最大长度</div>
            </div>
          </>
        )}

        {/* 代码执行节点的配置 */}
        {selectedNode.type === 'code' && (
          <>
            <div className="config-section">
              <label>编程语言</label>
              <select
                value={codeConfig.language}
                onChange={(e) => updateCodeConfig('language', e.target.value)}
                aria-label="选择编程语言"
              >
                <option value="Python">Python</option>
                <option value="JavaScript">JavaScript / Node.js</option>
              </select>
              <div className="config-hint">选择代码执行的编程语言</div>
            </div>

            <div className="config-section">
              <label>代码内容</label>
              <textarea
                value={codeConfig.code}
                onChange={(e) => updateCodeConfig('code', e.target.value)}
                placeholder="请输入您的代码"
                rows={12}
                style={{ fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace', fontSize: '13px' }}
              />
              <div className="config-hint">
                {codeConfig.language === 'Python' 
                  ? '请定义 main() 函数作为入口点，返回字典格式的结果'
                  : '请定义 main() 函数作为入口点，返回对象格式的结果'
                }
              </div>
            </div>

            <div className="config-section">
              <label>执行超时 (秒)</label>
              <input
                type="number"
                min="1"
                max="300"
                value={codeConfig.timeout}
                onChange={(e) => updateCodeConfig('timeout', parseInt(e.target.value))}
                placeholder="请输入超时时间"
              />
              <div className="config-hint">代码执行的最大允许时间（1-300秒）</div>
            </div>
          </>
        )}

        {/* 条件判断节点的配置 */}
        {selectedNode.type === 'condition' && (
          <>
            <div className="config-section">
              <div className="conditions-config-header">
                <label>判断条件</label>
                <button className="add-condition-btn" onClick={addCondition}>
                  + 添加条件
                </button>
              </div>
              
              {conditionConfig.conditions.length === 0 ? (
                <div className="no-conditions">
                  <p>暂无判断条件，点击"添加条件"开始配置</p>
                </div>
              ) : (
                <div className="conditions-config-list">
                  {conditionConfig.conditions.map((condition: any, index: number) => (
                    <div key={index} className="condition-config-item">
                      <div className="condition-config-header">
                        <span className="condition-index">条件 {index + 1}</span>
                        <button 
                          className="remove-condition-btn"
                          onClick={() => removeCondition(index)}
                        >
                          ×
                        </button>
                      </div>
                      
                      <div className="condition-config-fields">
                        <div className="field-group">
                          <label>变量名称</label>
                          <input
                            type="text"
                            value={condition.variable}
                            onChange={(e) => updateCondition(index, 'variable', e.target.value)}
                            placeholder="请输入要判断的变量名"
                          />
                        </div>
                        
                        <div className="field-group">
                          <label>判断操作</label>
                          <select
                            value={condition.operator}
                            onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                            aria-label="选择判断操作"
                          >
                            <option value="contains">包含</option>
                            <option value="not_contains">不包含</option>
                            <option value="starts_with">开始于</option>
                            <option value="ends_with">结束于</option>
                            <option value="is">等于</option>
                            <option value="is_not">不等于</option>
                            <option value="is_empty">为空</option>
                            <option value="is_not_empty">不为空</option>
                            <option value="greater_than">大于</option>
                            <option value="less_than">小于</option>
                          </select>
                        </div>
                        
                        <div className="field-group">
                          <label>比较值</label>
                          <input
                            type="text"
                            value={condition.value}
                            onChange={(e) => updateCondition(index, 'value', e.target.value)}
                            placeholder="请输入比较值"
                          />
                        </div>
                        
                        {index < conditionConfig.conditions.length - 1 && (
                          <div className="field-group">
                            <label>逻辑关系</label>
                            <select
                              value={condition.logicalOperator}
                              onChange={(e) => updateCondition(index, 'logicalOperator', e.target.value)}
                              aria-label="选择逻辑关系"
                            >
                              <option value="and">并且 (AND)</option>
                              <option value="or">或者 (OR)</option>
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/**
 * 节点类型定义
 */
const nodeTypes = {
  start: StartNode,
  end: EndNode,
  llm: LLMNode,
  code: CodeNode,
  condition: ConditionNode,
};

/**
 * 初始节点配置 - 开始节点和结束节点
 */
const initialNodes: Node[] = [
  {
    id: 'start-node',
    type: 'start',
    data: { 
      label: '开始', 
      description: '工作流的起始节点',
      variables: [
        {
          name: '用户输入',
          type: 'text',
          description: '用户的输入文本',
          required: true,
        },
        {
          name: '处理模式',
          type: 'text',
          description: '数据处理模式选择',
          required: false,
        }
      ]
    },
    position: { x: 100, y: 200 },
    deletable: false,
  },
  {
    id: 'end-node',
    type: 'end',
    data: { label: '结束', description: '工作流的结束节点' },
    position: { x: 700, y: 200 },
    deletable: false,
  },
];

/**
 * 初始连接线配置 - 去掉动画
 */
const initialEdges: Edge[] = [];

/**
 * 节点ID生成器
 */
let id = 2;
const getId = () => `dndnode_${++id}`;

/**
 * 拖拽流程组件
 */
const DnDFlow: React.FC = () => {
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const { project } = useReactFlow();

  /**
   * 自定义节点变化处理，防止删除开始和结束节点
   */
  const handleNodesChange = useCallback((changes: any[]) => {
    // 过滤掉删除开始节点和结束节点的操作
    const filteredChanges = changes.filter(change => {
      if (change.type === 'remove') {
        const nodeToRemove = nodes.find(node => node.id === change.id);
        return nodeToRemove?.type !== 'start' && nodeToRemove?.type !== 'end';
      }
      return true;
    });
    
    onNodesChange(filteredChanges);
  }, [nodes, onNodesChange]);

  /**
   * 确保始终有开始和结束节点
   */
  const ensureStartEndNodes = useCallback(() => {
    const hasStart = nodes.some(node => node.type === 'start');
    const hasEnd = nodes.some(node => node.type === 'end');
    
    const newNodes = [...nodes];
    
    if (!hasStart) {
      newNodes.push({
        id: 'start-node',
        type: 'start',
        data: { label: '开始', description: '工作流的起始节点' },
        position: { x: 100, y: 200 },
        deletable: false,
      });
    }
    
    if (!hasEnd) {
      newNodes.push({
        id: 'end-node',
        type: 'end',
        data: { label: '结束', description: '工作流的结束节点' },
        position: { x: 700, y: 200 },
        deletable: false,
      });
    }
    
    if (!hasStart || !hasEnd) {
      setNodes(newNodes);
    }
  }, [nodes, setNodes]);

  // 监听节点变化，确保始终有开始和结束节点
  React.useEffect(() => {
    ensureStartEndNodes();
  }, [ensureStartEndNodes]);

  /**
   * 处理节点点击事件
   */
  const onNodeClick = useCallback((event: React.MouseEvent, node: Node) => {
    setSelectedNode(node);
  }, []);

  /**
   * 处理画布点击事件
   */
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
  }, []);

  /**
   * 更新节点数据
   */
  const onUpdateNode = useCallback((nodeId: string, newData: any) => {
    setNodes((nds) =>
      nds.map((node) =>
        node.id === nodeId ? { ...node, data: newData } : node
      )
    );
  }, [setNodes]);

  /**
   * 处理节点连接事件
   */
  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge = {
        ...params,
        type: 'smoothstep',
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  /**
   * 处理拖拽悬停事件
   */
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  /**
   * 处理拖拽放置事件
   */
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData('application/reactflow');

      if (typeof nodeType === 'undefined' || !nodeType) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      const position = project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      /**
       * 根据节点类型获取配置
       */
      const getNodeConfig = (type: string) => {
        switch (type) {
          case 'llm':
            return {
              label: '大模型',
              type: 'llm',
              description: '大语言模型处理节点',
              modelConfig: {
                model: 'gpt-3.5-turbo',
                temperature: 0.7,
                maxTokens: 1000,
                systemPrompt: '你是一个有用的AI助手，请根据用户的输入提供准确、有帮助的回答。',
                userPrompt: '{{用户输入}}',
              },
            };
          case 'code':
            return {
              label: '代码执行',
              type: 'code',
              description: '代码执行节点',
              codeConfig: {
                language: 'Python',
                code: '# 请输入您的代码\ndef main():\n    # 在这里编写您的逻辑\n    return {"result": "Hello World"}',
                timeout: 30,
              },
            };
          case 'condition':
            return {
              label: '条件判断',
              type: 'condition',
              description: '条件判断节点',
              conditionConfig: {
                conditions: [
                  {
                    variable: '',
                    operator: 'contains',
                    value: '',
                    logicalOperator: 'and',
                  }
                ],
              },
            };
          default:
            return {
              label: '大模型',
              type: 'llm',
              description: '大语言模型处理节点',
              modelConfig: {
                model: 'gpt-3.5-turbo',
                temperature: 0.7,
                maxTokens: 1000,
                systemPrompt: '你是一个有用的AI助手，请根据用户的输入提供准确、有帮助的回答。',
                userPrompt: '{{用户输入}}',
              },
            };
        }
      };

      const nodeConfig = getNodeConfig(nodeType);

      const newNode: Node = {
        id: getId(),
        type: nodeConfig.type,
        position,
        data: { 
          label: nodeConfig.label,
          description: nodeConfig.description,
          ...(nodeConfig.modelConfig && { modelConfig: nodeConfig.modelConfig }),
          ...(nodeConfig.codeConfig && { codeConfig: nodeConfig.codeConfig }),
          ...(nodeConfig.conditionConfig && { conditionConfig: nodeConfig.conditionConfig }),
        },
        deletable: true,
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [project, setNodes]
  );

  return (
    <div className="dndflow horizontal-layout">
      <Sidebar />
      <div className="reactflow-wrapper" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          fitView
          style={{ backgroundColor: '#f8fafc' }}
          zoomOnScroll={false}
          zoomOnPinch={false}
          zoomOnDoubleClick={false}
          panOnScroll={false}
          panOnScrollSpeed={0.5}
          preventScrolling={true}
          defaultEdgeOptions={{
            type: 'smoothstep',
            style: { stroke: '#94a3b8', strokeWidth: 2 },
          }}
        >
          <Controls 
            style={{
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            }}
            showZoom={true}
            showFitView={true}
            showInteractive={false}
          />
          <Background 
            variant={BackgroundVariant.Dots} 
            gap={20} 
            size={1}
            color="#e2e8f0"
          />
          <MiniMap 
            nodeColor={(node) => {
              switch (node.type) {
                case 'start': return '#6b7280';
                case 'end': return '#374151';
                case 'llm': return '#9ca3af';
                case 'code': return '#6b7280';
                case 'condition': return '#374151';
                default: return '#d1d5db';
              }
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
            style={{
              background: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
            pannable
            zoomable
            position="bottom-right"
          />
        </ReactFlow>
      </div>
      
      {/* 右侧配置面板 */}
      {selectedNode && (
        <NodeConfigPanel
          selectedNode={selectedNode}
          onClose={() => setSelectedNode(null)}
          onUpdateNode={onUpdateNode}
        />
      )}
    </div>
  );
};

/**
 * Flow页面主组件
 */
const FlowPage: React.FC = () => {
  return (
    <div className="flow-page">
      <div className="flow-header">
        <h1>🎨 可视化流程设计器</h1>
        <p>类似 Dify 的交互设计 - 点击节点查看配置，拖拽节点创建工作流</p>
      </div>
      <ReactFlowProvider>
        <DnDProvider>
          <DnDFlow />
        </DnDProvider>
      </ReactFlowProvider>
    </div>
  );
};

export default FlowPage; 